import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Volume2, VolumeX, ChevronRight, Sparkles, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';

// Captain mascot images - Female Captain V4
const CAPTAIN_STATES = {
  idle: 'https://static.prod-images.emergentagent.com/jobs/db6a4ebc-40bc-4305-921f-4c070adc35cc/images/3351b116f7c0b8179e923cc379036a707179827838fdc88774d90f77ef72deff.png',
  waving: 'https://static.prod-images.emergentagent.com/jobs/db6a4ebc-40bc-4305-921f-4c070adc35cc/images/5aa781b146841a36ede1db3cb15f47695e8d6c1beb70b972899bb22fed4f39a4.png',
  sleeping: 'https://static.prod-images.emergentagent.com/jobs/db6a4ebc-40bc-4305-921f-4c070adc35cc/images/6ddf45fb5dc027266afcd036091689ddfa546affc30ef73ac92e425acf12bf07.png',
  happy: 'https://static.prod-images.emergentagent.com/jobs/db6a4ebc-40bc-4305-921f-4c070adc35cc/images/c8de41056b115634384b39ab7468cc7324985f4c7d49dddbbaaada4cd9dce023.png',
  thinking: 'https://static.prod-images.emergentagent.com/jobs/db6a4ebc-40bc-4305-921f-4c070adc35cc/images/8b2c0a747088e8c13919104b5cd6b83a5d160179991ae418a98d9767cdbb8bad.png',
};

// Mentor-style messages (calm, short, wise)
const MESSAGES = {
  welcome: [
    "Bienvenue à bord, marin.",
    "Content de te revoir.",
    "Bon vent pour aujourd'hui.",
  ],
  xp_gain: [
    "Cap validé.",
    "Tu avances bien.",
    "Continue comme ça.",
  ],
  badge: [
    "Tu prends du galon.",
    "Bien mérité.",
    "L'équipage est fier.",
  ],
  victory: [
    "Belle conquête.",
    "Victoire méritée.",
    "Bravo, capitaine.",
  ],
  level_up: [
    "Nouveau rang atteint.",
    "Tu montes en grade.",
    "Progression remarquable.",
  ],
  tip: [
    "Un bon marin vérifie sa progression.",
    "Les défis hebdo rapportent gros.",
    "Pense à féliciter l'équipage.",
    "Le calendrier t'attend.",
    "Partage tes victoires.",
    "Chaque jalon compte.",
  ],
  idle_wake: [
    "Hm? Tu es là?",
    "Ah, te revoilà.",
    "Prêt à naviguer?",
  ],
  encouragement: [
    "Mer agitée, garde le cap.",
    "Patience et persévérance.",
    "Chaque vague te rapproche.",
  ]
};

// Assistant mode - Onboarding steps
const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    message: "Bienvenue dans l'équipage ! Je suis ta Capitaine. Laisse-moi te guider.",
    action: null,
    completed_key: 'onboarding_welcome'
  },
  {
    id: 'profile',
    message: "Commence par personnaliser ton profil. C'est ta carte d'identité de marin.",
    action: '/profile',
    action_label: 'Mon profil',
    completed_key: 'profile_visited'
  },
  {
    id: 'channels',
    message: "Explore les canaux pour échanger avec l'équipage.",
    action: '/channels',
    action_label: 'Voir les canaux',
    completed_key: 'channels_visited'
  },
  {
    id: 'calendar',
    message: "Consulte le calendrier. Les événements live donnent des XP bonus.",
    action: '/calendar',
    action_label: 'Calendrier',
    completed_key: 'calendar_visited'
  },
  {
    id: 'victory',
    message: "Partage ta première victoire. L'équipage adore célébrer !",
    action: '/victories',
    action_label: 'Mes victoires',
    completed_key: 'victories_visited'
  },
  {
    id: 'complete',
    message: "Formation terminée ! Tu es prêt à naviguer. Bon vent, marin.",
    action: null,
    completed_key: 'onboarding_complete',
    reward: { badge: 'mousse_forme', xp: 50 }
  }
];

const CaptainMascot = ({ 
  onEvent, // Function to trigger events from parent: 'welcome', 'xp', 'badge', 'victory', 'level_up'
  disabled = false,
  userName = 'Marin'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Load initial preferences from localStorage
  const getInitialVisible = () => {
    const saved = localStorage.getItem('captain_visible');
    return saved !== null ? saved === 'true' : true;
  };
  
  const getInitialMuted = () => {
    const saved = localStorage.getItem('captain_muted');
    return saved !== null ? saved === 'true' : false;
  };
  
  const getOnboardingProgress = () => {
    const saved = localStorage.getItem('captain_onboarding');
    if (!saved) return { currentStep: 0, completedSteps: [] };
    try {
      const parsed = JSON.parse(saved);
      // Handle legacy "completed" string
      if (typeof parsed === 'string' || parsed.completed) {
        return { currentStep: 999, completedSteps: ['all'], completed: true };
      }
      return parsed;
    } catch (e) {
      // If not valid JSON (e.g., "completed" string), treat as completed
      return { currentStep: 999, completedSteps: ['all'], completed: true };
    }
  };
  
  const getInitialSize = () => {
    const saved = localStorage.getItem('captain_size');
    return saved || 'medium'; // 'small', 'medium', 'large'
  };

  const [state, setState] = useState('idle');
  const [message, setMessage] = useState(null);
  const [isVisible, setIsVisible] = useState(getInitialVisible);
  const [isMuted, setIsMuted] = useState(getInitialMuted);
  const [showSettings, setShowSettings] = useState(false);
  const [assistantMode, setAssistantMode] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState(getOnboardingProgress);
  const [showOnboardingBubble, setShowOnboardingBubble] = useState(false);
  const [avatarSize, setAvatarSize] = useState(getInitialSize);
  const [showFullAvatar, setShowFullAvatar] = useState(false);
  const lastActivityRef = useRef(null);
  const sleepTimeoutRef = useRef(null);
  const messageTimeoutRef = useRef(null);
  const hasGreetedRef = useRef(false);

  // Size configurations
  const sizeConfig = {
    small: { container: 'w-14 h-14', image: 'w-12 h-12', padding: 'p-1.5' },
    medium: { container: 'w-20 h-20', image: 'w-16 h-16', padding: 'p-2' },
    large: { container: 'w-24 h-24', image: 'w-20 h-20', padding: 'p-2.5' }
  };
  
  const currentSize = sizeConfig[avatarSize] || sizeConfig.medium;
  
  // Change avatar size
  const cycleSize = () => {
    const sizes = ['small', 'medium', 'large'];
    const currentIndex = sizes.indexOf(avatarSize);
    const nextIndex = (currentIndex + 1) % sizes.length;
    const newSize = sizes[nextIndex];
    setAvatarSize(newSize);
    localStorage.setItem('captain_size', newSize);
  };

  // Initialize lastActivityRef on mount
  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Get random message from category
  const getRandomMessage = useCallback((category) => {
    const msgs = MESSAGES[category];
    return msgs[Math.floor(Math.random() * msgs.length)];
  }, []);

  // Show message with auto-hide
  const showMessage = useCallback((text, duration = 4000) => {
    if (isMuted) return;
    
    setMessage(text);
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }
    messageTimeoutRef.current = setTimeout(() => {
      setMessage(null);
    }, duration);
  }, [isMuted]);
  
  // Check if onboarding should be active
  useEffect(() => {
    const progress = getOnboardingProgress();
    // Check if completed, skipped, or tutorial already done
    if (progress.completedSteps?.includes('onboarding_complete') || progress.skipped) {
      setAssistantMode(false);
      return;
    }
    // Also check tutorial status from localStorage
    try {
      const tutorialData = localStorage.getItem('odys_tutorial');
      if (tutorialData) {
        const parsed = JSON.parse(tutorialData);
        if (parsed.completed || parsed.skipped) {
          setAssistantMode(false);
          return;
        }
      }
    } catch (e) {}
    
    if (progress.currentStep < ONBOARDING_STEPS.length) {
      setAssistantMode(true);
      const timer = setTimeout(() => {
        setShowOnboardingBubble(true);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  // Track page visits for onboarding
  useEffect(() => {
    if (!assistantMode) return;
    
    const currentPath = location.pathname;
    const progress = { ...onboardingProgress };
    let updated = false;
    
    // Check if current page completes a step
    if (currentPath === '/profile' && !progress.completedSteps.includes('profile_visited')) {
      progress.completedSteps.push('profile_visited');
      updated = true;
    } else if (currentPath === '/channels' && !progress.completedSteps.includes('channels_visited')) {
      progress.completedSteps.push('channels_visited');
      updated = true;
    } else if (currentPath === '/calendar' && !progress.completedSteps.includes('calendar_visited')) {
      progress.completedSteps.push('calendar_visited');
      updated = true;
    } else if (currentPath === '/victories' && !progress.completedSteps.includes('victories_visited')) {
      progress.completedSteps.push('victories_visited');
      updated = true;
    }
    
    if (updated) {
      // Move to next step
      const nextStep = ONBOARDING_STEPS.findIndex(
        step => !progress.completedSteps.includes(step.completed_key)
      );
      progress.currentStep = nextStep >= 0 ? nextStep : ONBOARDING_STEPS.length - 1;
      
      setOnboardingProgress(progress);
      localStorage.setItem('captain_onboarding', JSON.stringify(progress));
      
      // Show encouragement
      setState('happy');
      showMessage("Bien joué ! Continue comme ça.");
      setTimeout(() => setState('idle'), 2500);
    }
  }, [location.pathname, assistantMode, onboardingProgress, showMessage]);
  
  // Get current onboarding step
  const currentOnboardingStep = ONBOARDING_STEPS[onboardingProgress.currentStep] || ONBOARDING_STEPS[0];
  const onboardingPercentage = Math.round((onboardingProgress.completedSteps.length / ONBOARDING_STEPS.length) * 100);
  
  // Complete onboarding
  const completeOnboarding = useCallback(() => {
    const progress = { ...onboardingProgress };
    progress.completedSteps.push('onboarding_complete');
    progress.currentStep = ONBOARDING_STEPS.length;
    setOnboardingProgress(progress);
    localStorage.setItem('captain_onboarding', JSON.stringify(progress));
    setAssistantMode(false);
    setShowOnboardingBubble(false);
    
    // Celebration
    setState('happy');
    showMessage("Formation terminée ! +50 XP 🎉");
    setTimeout(() => setState('idle'), 3500);
  }, [onboardingProgress, showMessage]);
  
  // Navigate to onboarding action
  const handleOnboardingAction = useCallback(() => {
    if (currentOnboardingStep.action) {
      navigate(currentOnboardingStep.action);
      setShowOnboardingBubble(false);
    } else if (currentOnboardingStep.id === 'complete') {
      completeOnboarding();
    } else {
      // Just acknowledge welcome
      const progress = { ...onboardingProgress };
      progress.completedSteps.push('onboarding_welcome');
      progress.currentStep = 1;
      setOnboardingProgress(progress);
      localStorage.setItem('captain_onboarding', JSON.stringify(progress));
    }
  }, [currentOnboardingStep, navigate, completeOnboarding, onboardingProgress]);

  // Trigger mascot reaction
  const triggerReaction = useCallback((eventType) => {
    lastActivityRef.current = Date.now();
    
    switch(eventType) {
      case 'welcome':
        setState('waving');
        showMessage(getRandomMessage('welcome'));
        setTimeout(() => setState('idle'), 3000);
        break;
      case 'xp':
        setState('happy');
        showMessage(getRandomMessage('xp_gain'));
        setTimeout(() => setState('idle'), 2500);
        break;
      case 'badge':
        setState('happy');
        showMessage(getRandomMessage('badge'));
        setTimeout(() => setState('idle'), 3000);
        break;
      case 'victory':
        setState('happy');
        showMessage(getRandomMessage('victory'));
        setTimeout(() => setState('idle'), 3000);
        break;
      case 'level_up':
        setState('happy');
        showMessage(getRandomMessage('level_up'));
        setTimeout(() => setState('idle'), 3500);
        break;
      case 'tip':
        setState('thinking');
        showMessage(getRandomMessage('tip'));
        setTimeout(() => setState('idle'), 4000);
        break;
      case 'wake':
        setState('idle');
        showMessage(getRandomMessage('idle_wake'));
        break;
      default:
        break;
    }
  }, [showMessage, getRandomMessage]);

  // Expose trigger function via ref or callback
  useEffect(() => {
    if (onEvent) {
      // Subscribe to events from parent
      window.captainMascotTrigger = triggerReaction;
    }
    return () => {
      window.captainMascotTrigger = null;
    };
  }, [onEvent, triggerReaction]);

  // Welcome greeting on first mount
  useEffect(() => {
    if (!hasGreetedRef.current && !disabled) {
      const timer = setTimeout(() => {
        triggerReaction('welcome');
        hasGreetedRef.current = true;
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [disabled, triggerReaction]);

  // Sleep after inactivity
  useEffect(() => {
    if (disabled) return;

    const checkInactivity = () => {
      const now = Date.now();
      const inactive = now - lastActivityRef.current > 30000; // 30 seconds
      
      if (inactive && state !== 'sleeping') {
        setState('sleeping');
        setMessage(null);
      }
    };

    const interval = setInterval(checkInactivity, 5000);
    return () => clearInterval(interval);
  }, [state, disabled]);

  // Wake up on mouse move
  useEffect(() => {
    if (disabled) return;

    const handleActivity = () => {
      if (state === 'sleeping') {
        lastActivityRef.current = Date.now();
        triggerReaction('wake');
      } else {
        lastActivityRef.current = Date.now();
      }
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
    };
  }, [state, disabled, triggerReaction]);

  // Random tips every 2-3 minutes (only if idle)
  useEffect(() => {
    if (disabled || isMuted) return;

    const tipInterval = setInterval(() => {
      if (state === 'idle' && Math.random() > 0.6) {
        triggerReaction('tip');
      }
    }, 150000); // Every 2.5 minutes

    return () => clearInterval(tipInterval);
  }, [state, disabled, isMuted, triggerReaction]);

  // Save preferences
  const toggleVisibility = () => {
    const newValue = !isVisible;
    setIsVisible(newValue);
    localStorage.setItem('captain_visible', newValue.toString());
  };

  const toggleMute = () => {
    const newValue = !isMuted;
    setIsMuted(newValue);
    localStorage.setItem('captain_muted', newValue.toString());
    if (newValue) setMessage(null);
  };

  if (disabled || !isVisible) {
    return (
      <button
        onClick={toggleVisibility}
        className="fixed bottom-32 md:bottom-20 left-6 z-50 p-2 bg-card/80 backdrop-blur-sm border border-border rounded-full hover:bg-card transition-all opacity-50 hover:opacity-100"
        title="Réactiver le Capitaine"
      >
        <img 
          src={CAPTAIN_STATES.idle} 
          alt="Captain" 
          className="w-8 h-8 object-contain grayscale"
        />
      </button>
    );
  }

  return (
    <div className="fixed bottom-32 md:bottom-20 left-6 z-50" data-testid="captain-mascot">
      {/* Onboarding Assistant Bubble */}
      <AnimatePresence>
        {showOnboardingBubble && assistantMode && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            className="absolute bottom-full left-0 mb-16 w-72"
          >
            <div className="bg-gradient-to-br from-card/98 to-card/95 backdrop-blur-xl border border-primary/30 rounded-2xl rounded-br-sm shadow-xl shadow-primary/10 overflow-hidden">
              {/* Progress bar */}
              <div className="h-1 bg-secondary">
                <motion.div 
                  className="h-full bg-gradient-to-r from-primary to-cyan-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${onboardingPercentage}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              
              <div className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="text-xs font-medium text-primary">
                      Formation • Étape {onboardingProgress.currentStep + 1}/{ONBOARDING_STEPS.length}
                    </span>
                  </div>
                  <button 
                    onClick={() => setShowOnboardingBubble(false)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Message */}
                <p className="text-sm text-foreground mb-4 leading-relaxed">
                  {currentOnboardingStep.message}
                </p>
                
                {/* Action button */}
                {currentOnboardingStep.action_label ? (
                  <Button 
                    size="sm" 
                    className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                    onClick={handleOnboardingAction}
                  >
                    {currentOnboardingStep.action_label}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : currentOnboardingStep.id === 'complete' ? (
                  <Button 
                    size="sm" 
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500"
                    onClick={handleOnboardingAction}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Terminer la formation
                  </Button>
                ) : (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="w-full"
                    onClick={handleOnboardingAction}
                  >
                    Compris !
                  </Button>
                )}
                
                {/* Skip option */}
                {onboardingProgress.currentStep < ONBOARDING_STEPS.length - 1 && (
                  <button 
                    onClick={() => {
                      setAssistantMode(false);
                      setShowOnboardingBubble(false);
                      localStorage.setItem('captain_onboarding', JSON.stringify({
                        ...onboardingProgress,
                        skipped: true,
                        completedSteps: [...(onboardingProgress.completedSteps || []), 'onboarding_complete']
                      }));
                    }}
                    className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Passer la formation
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Message bubble */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            className="absolute bottom-full left-0 mb-16 max-w-[200px]"
          >
            <div className="bg-card/95 backdrop-blur-xl border border-primary/20 rounded-2xl rounded-br-sm px-4 py-2.5 shadow-lg">
              <p className="text-sm text-foreground font-medium">{message}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings menu */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="absolute bottom-full right-0 mb-2 bg-card/95 backdrop-blur-xl border border-border rounded-xl p-2 shadow-lg"
          >
            <div className="flex gap-1">
              {/* Show assistant button if available */}
              {assistantMode && !showOnboardingBubble && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary"
                  onClick={() => {
                    setShowOnboardingBubble(true);
                    setShowSettings(false);
                  }}
                  title="Continuer la formation"
                >
                  <Sparkles className="w-4 h-4" />
                </Button>
              )}
              {/* Size toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={cycleSize}
                title={`Taille: ${avatarSize === 'small' ? 'Petite' : avatarSize === 'medium' ? 'Moyenne' : 'Grande'} (cliquer pour changer)`}
              >
                {avatarSize === 'large' ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={toggleMute}
                title={isMuted ? "Activer les messages" : "Désactiver les messages"}
              >
                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                onClick={toggleVisibility}
                title="Masquer la Capitaine"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Captain avatar */}
      <motion.div
        className="relative cursor-pointer group"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowSettings(!showSettings)}
        onDoubleClick={() => setShowFullAvatar(true)}
        animate={state === 'sleeping' ? { y: [0, -3, 0] } : { y: 0 }}
        transition={state === 'sleeping' ? { 
          y: { repeat: Infinity, duration: 2, ease: "easeInOut" }
        } : {}}
      >
        <div className="relative">
          {/* Outer glow ring */}
          <div className={`absolute -inset-2 rounded-full transition-all duration-500 ${
            state === 'happy' ? 'bg-gradient-to-r from-primary/40 via-yellow-500/30 to-primary/40 blur-xl' :
            state === 'sleeping' ? 'bg-blue-500/20 blur-lg' :
            state === 'waving' ? 'bg-gradient-to-r from-primary/30 to-cyan-500/30 blur-xl' :
            'bg-primary/20 blur-lg group-hover:bg-primary/30'
          }`} />
          
          {/* Glass container */}
          <div className={`relative ${currentSize.padding} rounded-2xl backdrop-blur-sm transition-all duration-300 ${
            state === 'happy' ? 'bg-gradient-to-br from-primary/20 to-yellow-500/10 border border-primary/30 shadow-lg shadow-primary/20' :
            state === 'sleeping' ? 'bg-blue-950/40 border border-blue-500/20' :
            state === 'waving' ? 'bg-gradient-to-br from-primary/15 to-cyan-500/10 border border-primary/25 shadow-lg shadow-primary/15' :
            'bg-card/60 border border-primary/20 group-hover:border-primary/40 group-hover:bg-card/80'
          }`}>
            
            {/* Captain image */}
            <motion.img
              key={state}
              src={CAPTAIN_STATES[state]}
              alt="Capitaine"
              className={`${currentSize.image} object-contain relative z-10 drop-shadow-lg`}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            />

            {/* Sleeping Zzz */}
            {state === 'sleeping' && (
              <motion.span
                className="absolute -top-1 -right-1 text-base"
                animate={{ opacity: [0.5, 1, 0.5], y: [0, -5, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                💤
              </motion.span>
            )}
            
            {/* Happy sparkles */}
            {state === 'happy' && (
              <motion.span
                className="absolute -top-1 -right-1 text-base"
                animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                ✨
              </motion.span>
            )}
          </div>
        </div>
      </motion.div>
      
      {/* Full avatar modal on double-click */}
      <AnimatePresence>
        {showFullAvatar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setShowFullAvatar(false)}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Large glow */}
              <div className="absolute -inset-8 bg-gradient-to-r from-primary/30 via-cyan-500/20 to-primary/30 rounded-full blur-3xl" />
              
              {/* Large avatar */}
              <div className="relative bg-card/90 backdrop-blur-xl border-2 border-primary/30 rounded-3xl p-6 shadow-2xl">
                <img
                  src={CAPTAIN_STATES[state]}
                  alt="Capitaine"
                  className="w-48 h-48 object-contain drop-shadow-2xl"
                />
                <p className="text-center mt-4 text-sm text-muted-foreground">
                  {state === 'sleeping' ? "💤 La Capitaine se repose..." : 
                   state === 'happy' ? "✨ La Capitaine célèbre !" :
                   state === 'thinking' ? "🤔 La Capitaine réfléchit..." :
                   state === 'waving' ? "👋 La Capitaine vous salue !" :
                   "⚓ Votre guide dans l'Odyssée"}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => setShowFullAvatar(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Helper function to trigger captain reactions from anywhere in the app
export const triggerCaptain = (eventType) => {
  if (window.captainMascotTrigger) {
    window.captainMascotTrigger(eventType);
  }
};

export default CaptainMascot;
